import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, Save, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function EmailSettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .in('setting_key', [
        'email_provider',
        'email_smtp_host',
        'email_smtp_port',
        'email_smtp_user',
        'email_smtp_password',
        'email_from_address',
        'email_from_name'
      ]);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: any) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le configurazioni email sono state aggiornate',
      });
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/settings')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurazione Email</h1>
              <p className="text-gray-600 mt-1">Configura il servizio email per notifiche e comunicazioni</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Server SMTP</CardTitle>
            <CardDescription>
              Configura il server SMTP per l'invio di email dalla piattaforma
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_provider">Provider Email</Label>
              <select
                id="email_provider"
                value={settings['email_provider'] || 'smtp'}
                onChange={(e) => handleSettingChange('email_provider', e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="smtp">SMTP Generico</option>
                <option value="sendgrid">SendGrid</option>
                <option value="mailgun">Mailgun</option>
              </select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email_smtp_host">Host SMTP</Label>
                <Input
                  id="email_smtp_host"
                  type="text"
                  value={settings['email_smtp_host'] || ''}
                  onChange={(e) => handleSettingChange('email_smtp_host', e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email_smtp_port">Porta SMTP</Label>
                <Input
                  id="email_smtp_port"
                  type="number"
                  value={settings['email_smtp_port'] || ''}
                  onChange={(e) => handleSettingChange('email_smtp_port', e.target.value)}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_smtp_user">Username SMTP</Label>
              <Input
                id="email_smtp_user"
                type="text"
                value={settings['email_smtp_user'] || ''}
                onChange={(e) => handleSettingChange('email_smtp_user', e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_smtp_password">Password SMTP</Label>
              <div className="relative">
                <Input
                  id="email_smtp_password"
                  type={showSecrets['email_smtp_password'] ? 'text' : 'password'}
                  value={settings['email_smtp_password'] || ''}
                  onChange={(e) => handleSettingChange('email_smtp_password', e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('email_smtp_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['email_smtp_password'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_from_address">Email Mittente</Label>
              <Input
                id="email_from_address"
                type="email"
                value={settings['email_from_address'] || ''}
                onChange={(e) => handleSettingChange('email_from_address', e.target.value)}
                placeholder="noreply@tuodominio.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_from_name">Nome Mittente</Label>
              <Input
                id="email_from_name"
                type="text"
                value={settings['email_from_name'] || ''}
                onChange={(e) => handleSettingChange('email_from_name', e.target.value)}
                placeholder="Nome Piattaforma"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Provider SMTP Consigliati</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>Gmail:</strong> smtp.gmail.com - Porta 587 (richiede app password)</li>
                  <li><strong>SendGrid:</strong> smtp.sendgrid.net - Porta 587</li>
                  <li><strong>Mailgun:</strong> smtp.mailgun.org - Porta 587</li>
                  <li><strong>Amazon SES:</strong> email-smtp.region.amazonaws.com - Porta 587</li>
                </ul>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
